import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import MedicalHistoryForm from './MedicalHistoryForm';
import DentalExaminationForm from './DentalExaminationForm';
import ProcedureTrackingForm from './ProcedureTrackingForm';
import ReviewSubmitPage from './ReviewSubmitPage';
import { apiRequest } from '../../config/api';

const PatientFormsWizard = ({ 
  open, 
  onClose, 
  patientId,
  patientName,
  appointmentId, // Optional: ID of the appointment this submission is for
  onComplete // Called when all forms are done and Review page should be shown
}) => {
  const [currentStep, setCurrentStep] = useState(1); // 1 = Medical History, 2 = Dental Examination, 3 = Procedure Tracking, 4 = Review & Submit
  const [medicalHistoryData, setMedicalHistoryData] = useState(null);
  const [dentalExaminationData, setDentalExaminationData] = useState(null);
  const [proceduresData, setProceduresData] = useState(null);

  // Track if modal was just opened to reset only once
  const [wasClosed, setWasClosed] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Reset to step 1 only when modal first opens (not during navigation)
  useEffect(() => {
    if (open && wasClosed && !hasInitialized) {
      // Modal just opened for the first time, reset everything
      console.log('Modal opened, resetting to step 1');
      setCurrentStep(1);
      setMedicalHistoryData(null);
      setDentalExaminationData(null);
      setProceduresData(null);
      setWasClosed(false);
      setHasInitialized(true);
    } else if (!open) {
      // Modal closed, mark it so we can reset next time it opens
      setWasClosed(true);
      setHasInitialized(false);
    }
    // Don't reset step when open changes if we're already initialized
  }, [open, wasClosed, hasInitialized]);
  
  // Debug: Log current step changes
  useEffect(() => {
    console.log('Current step changed to:', currentStep);
  }, [currentStep]);

  // Fetch existing data when modal opens (only if we're on step 1)
  useEffect(() => {
    if (open && patientId && currentStep === 1) {
      fetchAllData();
    }
  }, [open, patientId, appointmentId, currentStep]);

  const fetchAllData = async () => {
    if (!patientId) return;

    try {
      // Fetch medical history - include appointment_id if available
      try {
        const medicalHistoryUrl = appointmentId 
          ? `get_medical_history.php?patient_id=${patientId}&appointment_id=${appointmentId}`
          : `get_medical_history.php?patient_id=${patientId}`;
        const mhData = await apiRequest(medicalHistoryUrl);
        if (mhData.success && mhData.medical_history) {
          setMedicalHistoryData(mhData.medical_history);
        } else if (mhData.success && mhData.history) {
          // Fallback for backward compatibility
          setMedicalHistoryData(mhData.history);
        } else {
          // No medical history found - set to null so form uses defaults
          setMedicalHistoryData(null);
        }
      } catch (error) {
        console.error('Error fetching medical history:', error);
        setMedicalHistoryData(null);
      }

      // Fetch dental examination
      try {
        const deData = await apiRequest(`get_dental_examination.php?patient_id=${patientId}`);
        if (deData.success && deData.examination) {
          setDentalExaminationData(deData.examination);
        } else {
          // No dental examination found - set to null so form uses defaults
          setDentalExaminationData(null);
        }
      } catch (error) {
        console.error('Error fetching dental examination:', error);
        setDentalExaminationData(null);
      }

      // Fetch procedure tracking
      try {
        const ptData = await apiRequest(`get_procedure_tracking.php?patient_id=${patientId}`);
        if (ptData.success && ptData.tracking && ptData.tracking.procedures && ptData.tracking.procedures.length > 0) {
          setProceduresData({
            rows: ptData.tracking.procedures || [],
            summary: ptData.tracking.summary || { total: 0, paid: 0, due: 0 }
          });
        } else {
          // No procedure tracking found - set to null so form uses defaults
          setProceduresData(null);
        }
      } catch (error) {
        console.error('Error fetching procedure tracking:', error);
        setProceduresData(null);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      // Reset all to null on error
      setMedicalHistoryData(null);
      setDentalExaminationData(null);
      setProceduresData(null);
    }
  };

  // Handle next step
  const handleNext = (step, data) => {
    console.log('🔵 handleNext called with step:', step, 'data:', data, 'currentStep:', currentStep);
    if (step === 1) {
      // Medical History completed, move to Dental Examination
      setMedicalHistoryData(data);
      setCurrentStep(2);
      console.log('✅ Moving to step 2: Dental Examination');
    } else if (step === 2) {
      // Dental Examination completed, move to Procedure Tracking
      setDentalExaminationData(data);
      setCurrentStep(3);
      console.log('✅ Moving to step 3: Procedure Tracking');
    } else if (step === 3) {
      // Procedure Tracking completed, move to Review & Submit
      console.log('🔵 Procedure Tracking completed, moving to Review & Submit. Data:', data);
      setProceduresData(data);
      // Use functional update to ensure state is set correctly
      setCurrentStep((prev) => {
        console.log('🔵 Setting step from', prev, 'to 4');
        return 4;
      });
      console.log('✅ Step set to 4: Review & Submit');
    }
  };

  // Handle back step
  const handleBack = (step) => {
    if (step === 2) {
      // Go back to Medical History
      setCurrentStep(1);
    } else if (step === 3) {
      // Go back to Dental Examination
      setCurrentStep(2);
    } else if (step === 4) {
      // Go back to Procedure Tracking
      setCurrentStep(3);
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Medical History';
      case 2:
        return 'Dental Examination';
      case 3:
        return 'Procedure Tracking & Payments';
      case 4:
        return 'Review & Submit';
      default:
        return '';
    }
  };

  // Get step description
  const getStepDescription = () => {
    switch (currentStep) {
      case 1:
        return 'Record patient medical history and conditions';
      case 2:
        return 'Record patient dental examination and findings';
      case 3:
        return 'Track dental procedures and payment details';
      case 4:
        return 'Review all information before final submission';
      default:
        return '';
    }
  };

  // Handle final submission
  const handleSubmit = async (result) => {
    // Clear all localStorage/sessionStorage entries related to patient forms
    if (typeof window !== "undefined") {
      try {
        // Clear procedure tracking drafts for this patient
        const draftKeyBase = "dental:procedureTracking";
        if (patientId) {
          // Clear specific patient draft
          const patientDraftKey = `${draftKeyBase}:${patientId}:draft`;
          localStorage.removeItem(patientDraftKey);
          
          // Also clear any drafts with appointmentId pattern
          if (appointmentId) {
            const appointmentDraftKey = `${draftKeyBase}:${appointmentId}:draft`;
            localStorage.removeItem(appointmentDraftKey);
          }
        }
        
        // Clear all procedure tracking drafts (in case of any orphaned entries)
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(draftKeyBase)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        
        // Clear sessionStorage as well (if any form data is stored there)
        if (sessionStorage.length > 0) {
          const sessionKeysToRemove = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes('dental') || key.includes('procedure') || key.includes('medical') || key.includes('patient'))) {
              sessionKeysToRemove.push(key);
            }
          }
          sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
        }
      } catch (error) {
        console.error('Error clearing storage:', error);
      }
    }
    
    // Reset all form data to default
    setMedicalHistoryData(null);
    setDentalExaminationData(null);
    setProceduresData(null);
    setCurrentStep(1);
    
    // Close modal after successful submission
    onClose();
    if (onComplete) {
      onComplete({
        type: 'submitted',
        patientId,
        patientName,
        result,
        submissionId: result?.submission_id
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-6xl max-h-[95vh] w-[98vw] p-0 flex flex-col">
        {/* Step Indicator - Always visible at top, outside scrollable area */}
        <div className="no-print flex items-center justify-center py-4 px-6 border-b border-gray-700 bg-gray-900/95 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center flex-wrap justify-center gap-2">
            {/* Step 1 */}
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                currentStep >= 1 ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-700 text-gray-400'
              }`}>
                1
              </div>
              <span className={`ml-2 text-sm whitespace-nowrap font-medium ${currentStep >= 1 ? 'text-white' : 'text-gray-400'}`}>
                Medical History
              </span>
            </div>
            
            <div className={`w-12 h-0.5 transition-all ${currentStep >= 2 ? 'bg-blue-600' : 'bg-gray-700'}`} />
            
            {/* Step 2 */}
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                currentStep >= 2 ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-700 text-gray-400'
              }`}>
                2
              </div>
              <span className={`ml-2 text-sm whitespace-nowrap font-medium ${currentStep >= 2 ? 'text-white' : 'text-gray-400'}`}>
                Dental Examination
              </span>
            </div>
            
            <div className={`w-12 h-0.5 transition-all ${currentStep >= 3 ? 'bg-blue-600' : 'bg-gray-700'}`} />
            
            {/* Step 3 */}
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                currentStep >= 3 ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-700 text-gray-400'
              }`}>
                3
              </div>
              <span className={`ml-2 text-sm whitespace-nowrap font-medium ${currentStep >= 3 ? 'text-white' : 'text-gray-400'}`}>
                Procedures & Payments
              </span>
            </div>
            
            <div className={`w-12 h-0.5 transition-all ${currentStep >= 4 ? 'bg-blue-600' : 'bg-gray-700'}`} />
            
            {/* Step 4 */}
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                currentStep >= 4 ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-700 text-gray-400'
              }`}>
                4
              </div>
              <span className={`ml-2 text-sm whitespace-nowrap font-medium ${currentStep >= 4 ? 'text-white' : 'text-gray-400'}`}>
                Review & Submit
              </span>
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Form Title and Description - Hidden on step 4 (Review & Submit) since ReviewSubmitPage has its own header */}
          {currentStep !== 4 && (
            <DialogHeader className="mb-4">
              <DialogTitle className="text-white text-xl sm:text-2xl">
                {getStepTitle()}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                {getStepDescription()}
              </DialogDescription>
            </DialogHeader>
          )}

          {/* Form Content */}
          <div className="mt-4">
          {currentStep === 1 && (
            <MedicalHistoryForm
              open={true}
              onClose={onClose}
              patientId={patientId}
              patientName={patientName}
              appointmentId={appointmentId}
              onNext={(data) => {
                handleNext(1, data);
              }}
              hideDialog={true} // Hide its own dialog since we're in a wrapper
            />
          )}

          {currentStep === 2 && (
            <DentalExaminationForm
              open={true}
              onClose={onClose}
              patientId={patientId}
              patientName={patientName}
              onBack={() => handleBack(2)}
              onComplete={(data) => {
                handleNext(2, data);
              }}
              hideDialog={true} // Hide its own dialog since we're in a wrapper
            />
          )}

          {currentStep === 3 && (
            <ProcedureTrackingForm
              open={true}
              onClose={onClose}
              patientId={patientId}
              patientName={patientName}
              appointmentId={appointmentId}
              medicalHistory={medicalHistoryData}
              dentalExamination={dentalExaminationData}
              onBack={() => handleBack(3)}
              onNext={(data) => {
                handleNext(3, data);
              }}
              hideDialog={true} // Hide its own dialog since we're in a wrapper
            />
          )}

          {currentStep === 4 && (
            <ReviewSubmitPage
              patientId={patientId}
              patientName={patientName}
              appointmentId={appointmentId}
              medicalHistory={medicalHistoryData}
              dentalExamination={dentalExaminationData}
              procedures={proceduresData}
              onBack={() => handleBack(4)}
              onSubmit={handleSubmit}
              hideDialog={true} // Render in modal mode
            />
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PatientFormsWizard;

